import { useState } from 'react'

import {
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'

import useCreateApiKey from '../hooks/useCreateApiKey'
import useDeleteApiKeys from '../hooks/useDeleteApiKeys'
import CopyToClipboardIconButton from 'common/components/ui/buttons/CopyToClipboardButton'

export interface ApiKeysTableRow {
  id: string
  isChecked?: boolean
  note?: string
  createdAt?: string
}
function apiKeyAddedTempRow(id: string | null) {
  if (id === null) {
    return
  } else {
    return (
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, fontSize: '1rem' }}>
        <TableCell component="th" sx={{ fontSize: '1rem' }}>
          ApiKey Added:
        </TableCell>
        <TableCell color="red" align="left" sx={{ fontSize: '1rem' }}>
          <TextField fullWidth defaultValue={id} value={id}></TextField>
          WARNING: You will only see this key once! Copy it somewhere safe.
        </TableCell>
        <TableCell align="right" sx={{ fontSize: '1rem' }}>
          <CopyToClipboardIconButton text={id} />
        </TableCell>
      </TableRow>
    )
  }
}
function errorTempRow(err: Error) {
  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, fontSize: '1rem' }}>
      <TableCell></TableCell>
      <TableCell align="left" color="orange" sx={{ fontSize: '1rem' }}>
        {err.message}
      </TableCell>
      <TableCell></TableCell>
    </TableRow>
  )
}

export function ApiKeysTable({
  rows,
  mutateFunction,
  updateRowFunction,
  account,
}: {
  rows: ApiKeysTableRow[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutateFunction: () => any
  updateRowFunction: (id: string, checked: boolean) => void
  account?: string
}) {
  const { deleteApiKeys, err: deleteApiKeyError } = useDeleteApiKeys()
  const { createApiKey, data: newApiKey, err: createApiKeyError } = useCreateApiKey(account)
  // setting default vals
  const [apiKeyDescription, setApiKeyDescription] = useState('')
  const [topCheckBoxChecked, setTopCheckBox] = useState(false)
  const [rowCheckBoxCounter, setRowCheckBoxCounter] = useState(0)
  const [showNewApiKeyRow, setShowNewApiKeyRow] = useState(false)

  return (
    <>
      <Paper elevation={3} sx={{ padding: '24px' }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableBody>
              <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, fontSize: '1rem' }}>
                <TableCell component="th" sx={{ fontSize: '1rem', color: 'primaryColors.actionGrey' }}>
                  Name
                </TableCell>
                <TableCell align="left" sx={{ fontSize: '1rem' }}>
                  <TextField
                    fullWidth
                    onChange={(newApiKeyDescription) => {
                      setApiKeyDescription(newApiKeyDescription.target.value)
                    }}
                    defaultValue={apiKeyDescription}
                    value={apiKeyDescription}
                  ></TextField>
                </TableCell>
                <TableCell align="right" sx={{ fontSize: '1rem' }}>
                  <Button
                    onClick={() => {
                      createApiKey(apiKeyDescription).then(() => {
                        mutateFunction()
                        setShowNewApiKeyRow(true)
                      })
                      setApiKeyDescription('')
                    }}
                  >
                    Add
                  </Button>
                </TableCell>
              </TableRow>
              {showNewApiKeyRow && apiKeyAddedTempRow(newApiKey)}
              {createApiKeyError && errorTempRow(createApiKeyError)}
              {deleteApiKeyError && errorTempRow(deleteApiKeyError)}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox
                  checked={topCheckBoxChecked}
                  onClick={() => {
                    setTopCheckBox(!topCheckBoxChecked)
                    rows?.map((row) => {
                      updateRowFunction(row.id, !topCheckBoxChecked)
                    })
                    if (topCheckBoxChecked) {
                      setRowCheckBoxCounter(0)
                    } else {
                      setRowCheckBoxCounter(rows.length)
                    }
                  }}
                ></Checkbox>
              </TableCell>
              <TableCell>Description</TableCell>
              {!account && (
                <TableCell>
                  <Button
                    disabled={rowCheckBoxCounter < 1}
                    onClick={() => {
                      const ids: string[] = []
                      rows?.map((row) => {
                        if (row.isChecked) {
                          ids.push(row.id)
                        }
                      })
                      deleteApiKeys(ids).then(() => {
                        mutateFunction()
                      })
                      setShowNewApiKeyRow(false)
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              )}
              <TableCell>Created At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row) => (
              <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, fontSize: '1rem' }}>
                <TableCell component="th" sx={{ fontSize: '1rem', color: 'primaryColors.actionGrey' }}>
                  <Checkbox
                    key={row.id}
                    checked={row.isChecked}
                    onClick={() => {
                      updateRowFunction(row.id, !row.isChecked)
                      if (row.isChecked) {
                        // if its being flipped to true in this call
                        setRowCheckBoxCounter(rowCheckBoxCounter + 1)
                      } else {
                        // else being flipped to false, subbtract
                        setRowCheckBoxCounter(rowCheckBoxCounter - 1)
                      }
                    }}
                  ></Checkbox>
                </TableCell>
                <TableCell sx={{ fontSize: '1rems' }}>{row.note}</TableCell>
                {!account && (
                  <TableCell sx={{ fontSize: '1rem' }}>
                    <Button
                      data-cy="delete-api-key-button"
                      onClick={() => {
                        deleteApiKeys([row.id]).then(() => {
                          mutateFunction()
                        })
                        setShowNewApiKeyRow(false)
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                )}
                <TableCell sx={{ fontSize: '1rem' }}>{new Date(Number(row.createdAt) * 1000).toString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
